import React from 'react'
import styles from './index.module.scss'
import type { CommonMsgNodeProps, MessageContent } from '@/types/message'
import starIcon from '@/assets/images/star-icon.svg'
import downloadIcon from '@/assets/images/download.svg'
import playIcon from './play.svg'
import { $t } from '@/i18n/intl'
import { message, Modal } from 'antd'
import { getStores } from '@/stores'
import { observer } from 'mobx-react'
import { getVideoBatchAPI } from '@/servers/agent-v2'
import { generateUUID } from '@/utils'
import { requestChatScrollToBottomAlways } from '@/pages/main/helpers/request-chat-scroll-to-bottom'
import Button from '@/components/button'
import type { VideoProductImagesItem } from '@/proxy-request/api'

import SkeletonScreen from './skeleton-screen'

type VideoTaskStatus = 'queued' | 'generating' | 'completed' | 'failed'
const RESUBMIT_MESSAGE_KEY = 'video-gen-card-resubmit'

interface VideoTaskInfo {
  cover_url?: string
  provider_task_id?: string
  status?: VideoTaskStatus | string | number
  video_url?: string
}

interface NormalizedVideoInfo {
  video_url: string
  video_cover: string
  video_id: string
  video_status: VideoTaskStatus
  provider_task_id?: string
}

interface NormalizedVideoGenCardValue {
  displayText: string
  videos: NormalizedVideoInfo[]
}

interface VideoBatchInfo {
  batch_id?: string
  duration?: number
  generate_count?: number
  language?: string
  model?: string
  product_desc?: string
  product_images?: Array<string | VideoProductImagesItem>
  ratio?: string
  script_mode?: string
}

interface VideoGenCardProps extends CommonMsgNodeProps {
  node: {
    value: {
      text?: string
      disabled?: boolean
      ext_data?: string
      title?: string
      model?: string
      status?: number | string
      desc?: string
      batch_id?: string
      batch_status?: string
      task_count?: number
      task_ids?: string[]
      tasks_info?: Record<string, VideoTaskInfo>
    }
  }
}

const isFailedBatchStatus = (batchStatus?: string) => batchStatus === 'failed'

enum VideoStatusEnum {
  queued = 'queued',
  generating = 'generating',
  completed = 'completed',
  failed = 'failed'
}

const normalizeStatus = (
  status: VideoTaskInfo['status'],
  fallbackStatus?: VideoGenCardProps['node']['value']['status']
): VideoTaskStatus => {
  if (status === 'queued' || status === 0 || fallbackStatus === 'queued' || fallbackStatus === 0) {
    return 'queued'
  }
  if (status === 'completed' || status === 2) return 'completed'
  if (status === 'failed' || status === 4) return 'failed'
  if (status === 'generating' || status === 1 || status === 3) return 'generating'
  if (fallbackStatus === 2) return 'completed'
  if (fallbackStatus === 4) return 'failed'
  return 'generating'
}

const normalizeVideoGenCardValue = (
  value: VideoGenCardProps['node']['value'] = {}
): NormalizedVideoGenCardValue => {
  const tasks = value.tasks_info ? Object.entries(value.tasks_info) : []
  const videos = tasks.length
    ? tasks.map(([videoId, task]) => ({
        video_url: task.video_url || '',
        video_cover: task.cover_url || '',
        video_id: videoId,
        video_status: normalizeStatus(task.status, value.status),
        provider_task_id: task.provider_task_id
      }))
    : (value.task_ids || []).map(videoId => ({
        video_url: '',
        video_cover: '',
        video_id: videoId,
        video_status: normalizeStatus(value.status, value.status)
      }))

  return {
    displayText: value.desc || value.text || '',
    videos
  }
}

const normalizeProductImages = (productImages?: VideoBatchInfo['product_images']) => {
  if (!Array.isArray(productImages)) {
    return {
      imageUrls: [],
      hasFaceList: []
    }
  }

  return productImages.reduce<{
    imageUrls: string[]
    hasFaceList: boolean[]
  }>(
    (result, item) => {
      if (typeof item === 'string') {
        result.imageUrls.push(item)
        result.hasFaceList.push(false)
        return result
      }

      if (item?.image_url) {
        result.imageUrls.push(item.image_url)
        result.hasFaceList.push(Boolean(item.has_face))
      }
      return result
    },
    {
      imageUrls: [],
      hasFaceList: []
    }
  )
}

const buildRetryVideoDescMessage = (batchInfo?: VideoBatchInfo): MessageContent => {
  const { imageUrls, hasFaceList } = normalizeProductImages(batchInfo?.product_images)
  const productDesc = typeof batchInfo?.product_desc === 'string' ? batchInfo.product_desc : ''
  const language = typeof batchInfo?.language === 'string' ? batchInfo.language : ''
  const extData = {
    ratio: batchInfo?.ratio,
    duration: batchInfo?.duration,
    language: batchInfo?.language,
    count: batchInfo?.generate_count,
    script_mode: batchInfo?.script_mode
  }

  return {
    id: generateUUID(),
    message_id: generateUUID(),
    sender: 'assistant',
    placement: 'start',
    content: [
      {
        type: 'block',
        typing: true,
        showAvatar: true,
        styleType: 'card',
        value: [
          {
            display: 'block',
            template_id: 'video_desc_card',
            type: 'template',
            version: '1.0',
            value: {
              title: '视频设置',
              desc: productDesc,
              has_face: hasFaceList,
              image_urls: imageUrls,
              status: 2,
              language,
              ext_data: JSON.stringify(extData)
            }
          }
        ]
      }
    ]
  }
}

const VideoGenCard = (props: VideoGenCardProps) => {
  const { node } = props
  const value = node?.value || {}
  const normalizedValue = normalizeVideoGenCardValue(value)
  const [playModalOpen, setPlayModalOpen] = React.useState(false)
  const modalVideoRef = React.useRef<HTMLVideoElement | null>(null)
  const [activeVideo, setActiveVideo] = React.useState<NormalizedVideoInfo | null>(null)
  const [resubmitLoading, setResubmitLoading] = React.useState(false)
  const { agentV2Store } = getStores()
  const isBatchFailed = isFailedBatchStatus(value.batch_status)

  const isVideoReady = (video: NormalizedVideoInfo) =>
    video.video_status === VideoStatusEnum.completed && Boolean(video.video_url)

  const handleOpenPlayModal = (video: NormalizedVideoInfo) => {
    if (!isVideoReady(video)) return
    setActiveVideo(video)
    setPlayModalOpen(true)
  }

  const handleClosePlayModal = () => {
    if (modalVideoRef.current && !modalVideoRef.current.paused) {
      modalVideoRef.current.pause()
    }
    setPlayModalOpen(false)
  }

  React.useEffect(() => {
    if (playModalOpen && modalVideoRef.current) {
      modalVideoRef.current.play().catch(() => {})
    }
  }, [playModalOpen])

  const handleDownloadVideo = async (video: NormalizedVideoInfo) => {
    const url = video.video_url
    if (!url) {
      message.warning($t('demo.videoLibrary.noVideoToDownload'))
      return
    }
    const fileName = `video_${video.video_id || Date.now()}.mp4`
    try {
      const res = await fetch(url, { mode: 'cors' })
      if (!res.ok) throw new Error(res.statusText)
      const blob = await res.blob()
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      link.click()
      URL.revokeObjectURL(blobUrl)
      message.success($t('demo.videoLibrary.downloadSuccess'))
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer')
      message.info($t('demo.videoLibrary.downloadOpenInNewTab'))
    }
  }

  const handleAddToLibrary = () => {
    agentV2Store.setVideoLibraryVisible(true)
  }

  const appendRetryVideoDescCard = (batchInfo?: VideoBatchInfo) => {
    agentV2Store.renderMsgList.push(buildRetryVideoDescMessage(batchInfo))
    setTimeout(() => {
      requestChatScrollToBottomAlways()
    }, 10)
  }

  const handleResubmit = async () => {
    if (resubmitLoading) return
    const batchId = value.batch_id
    if (!batchId) {
      appendRetryVideoDescCard()
      return
    }

    setResubmitLoading(true)
    try {
      message.loading({ key: RESUBMIT_MESSAGE_KEY, content: '正在获取原视频设置...', duration: 0 })
      const res = await getVideoBatchAPI({ batch_id: batchId })
      const batchInfo = res?.code === 0 ? res.data : undefined
      if (!batchInfo) {
        message.warning({
          key: RESUBMIT_MESSAGE_KEY,
          content: res?.message || '获取原视频设置失败，已打开空白设置'
        })
      } else {
        message.destroy(RESUBMIT_MESSAGE_KEY)
      }
      appendRetryVideoDescCard(batchInfo)
    } catch (err) {
      console.error('get video batch failed', err)
      message.warning({
        key: RESUBMIT_MESSAGE_KEY,
        content: '获取原视频设置失败，已打开空白设置'
      })
      appendRetryVideoDescCard()
    } finally {
      setResubmitLoading(false)
    }
  }

  const videos = normalizedValue.videos
  const allVideosReady = videos.length > 0 && videos.every(video => isVideoReady(video))

  const firstVideo = videos[0]
  const getPlaceholderLabel = (status: NormalizedVideoInfo['video_status']) => {
    if (status === VideoStatusEnum.queued) return '排队中'
    if (status === VideoStatusEnum.failed) return '生成失败'
    return '生成中'
  }

  return (
    <>
      <div className={styles.container}>
        {normalizedValue.displayText ? (
          <div className={styles.text}>{normalizedValue.displayText}</div>
        ) : null}
        <div className={styles.videoList}>
          {videos.map((video, index) => {
            const ready = isVideoReady(video)
            const key = video.video_id || video.provider_task_id || index
            return (
              <div className={styles.videoItem} key={key}>
                {/* 视频区域：仅展示封面，点击在弹层中播放 */}
                <div className={styles.videoContainer}>
                  {ready ? (
                    <>
                      {video.video_cover ? (
                        <img src={video.video_cover} alt="" className={styles.videoCover} />
                      ) : (
                        <video
                          className={styles.videoCover}
                          src={video.video_url}
                          preload="metadata"
                          muted
                          playsInline
                        />
                      )}
                      <div
                        className={styles.playButtonOverlay}
                        onClick={() => handleOpenPlayModal(video)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            handleOpenPlayModal(video)
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="播放视频"
                      >
                        <div className={styles.playButton}>
                          <img src={playIcon} alt="play" className={styles.playIcon} />
                        </div>
                      </div>
                    </>
                  ) : video.video_status === VideoStatusEnum.failed ? (
                    <div className={styles.placeholderState}>
                      {getPlaceholderLabel(video.video_status)}
                    </div>
                  ) : (
                    <div className={styles.placeholderState}>
                      {getPlaceholderLabel(video.video_status)}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* 按钮组区域 */}
        <div className={styles.buttonGroup}>
          <button className={styles.actionButton} onClick={handleAddToLibrary}>
            <img className={styles.icon} src={starIcon} alt="star" />
            <span className={styles.buttonText}>我的视频库</span>
          </button>
          <button
            className={styles.actionButton}
            onClick={() => firstVideo && handleDownloadVideo(firstVideo)}
            disabled={!allVideosReady}
          >
            <img className={styles.icon} src={downloadIcon} alt="chromecast" />
            <span className={styles.buttonText}>保存视频</span>
          </button>
        </div>
        {isBatchFailed ? (
          <Button className="y-mt-16 y-mb-16" onClick={handleResubmit} disabled={resubmitLoading}>
            <span>重新提交</span>
          </Button>
        ) : null}
      </div>

      <Modal
        open={playModalOpen}
        onCancel={handleClosePlayModal}
        footer={null}
        width="min(90vw, 720px)"
        centered
        destroyOnClose
        className={styles.playModal}
        styles={{ body: { padding: 0 } }}
      >
        {playModalOpen && activeVideo?.video_url && (
          <video
            ref={modalVideoRef}
            className={styles.modalVideo}
            src={activeVideo.video_url}
            controls
            autoPlay
            playsInline
            onEnded={handleClosePlayModal}
          />
        )}
      </Modal>
    </>
  )
}

const Main = (props: VideoGenCardProps) => {
  const { node } = props
  const { status = 1, title = '', batch_status } = node?.value || {}
  const loading = !isFailedBatchStatus(batch_status) && status === 3
  if (loading) {
    return <SkeletonScreen status={status as number} title={title} />
  }

  return <VideoGenCard {...props} />
}

export default observer(Main)
